import { useCallback, useEffect, useRef } from 'react'
import Split from 'split.js'
import './useSplitView.css'

type SplitViewOptions = {
  /** localStorage key for saving split proportions */
  storageKey: string
  /** Default split proportions [left%, right%] */
  defaultSizes?: [number, number]
  /** Minimum panel sizes [leftMin, rightMin] in pixels */
  minSizes?: [number, number]
  /** Gutter size in pixels */
  gutterSize?: number
  /** Whether the split view should be disabled */
  disabled?: boolean
}

type SplitViewReturn = {
  /** Ref for the left panel element */
  leftPanelRef: React.RefObject<HTMLDivElement>
  /** Ref for the right panel element */
  rightPanelRef: React.RefObject<HTMLDivElement>
  /** Whether the split is currently active */
  isSplitActive: boolean
}

export const useSplitView = ({
  storageKey,
  defaultSizes = [40, 60],
  minSizes = [300, 400],
  gutterSize = 16,
  disabled = false,
}: SplitViewOptions): SplitViewReturn => {
  const leftPanelRef = useRef<HTMLDivElement>(null)
  const rightPanelRef = useRef<HTMLDivElement>(null)
  const splitInstanceRef = useRef<Split.Instance | null>(null)

  const getSavedProportions = useCallback((): [number, number] => {
    try {
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        const proportions = saved.split(',').map(Number)
        if (proportions.length === 2 && proportions.every((n) => !isNaN(n))) {
          return proportions as [number, number]
        }
      }
    } catch {
      // Ignore
    }
    return defaultSizes
  }, [storageKey, defaultSizes])

  const saveProportions = useCallback(
    (sizes: number[]) => {
      try {
        const roundedSizes = sizes.map((size) => Math.round(size * 100) / 100)
        localStorage.setItem(storageKey, roundedSizes.join(','))
      } catch {
        // Ignore
      }
    },
    [storageKey]
  )

  useEffect(() => {
    const initializeSplit = () => {
      if (
        leftPanelRef.current &&
        rightPanelRef.current &&
        !splitInstanceRef.current &&
        !disabled
      ) {
        const savedProportions = getSavedProportions()
        splitInstanceRef.current = Split(
          [leftPanelRef.current, rightPanelRef.current],
          {
            sizes: savedProportions,
            minSize: minSizes,
            gutterSize,
            cursor: 'col-resize',
            direction: 'horizontal',
            elementStyle: (_dimension, size) => ({
              'flex-basis':
                size === 0 ? '0px' : `calc(${size}% - ${gutterSize / 2}px)`,
            }),
            gutterStyle: (dimension, gutterSize) => ({
              [`${dimension}`]: `${gutterSize}px`,
            }),
            onDragEnd: (sizes) => {
              saveProportions(sizes)
            },
          }
        )

        const createIndicator = (gutter: HTMLElement) => {
          if (!gutter || gutter.querySelector('[data-gutter-indicator]')) {
            return
          }

          const indicator = document.createElement('div')
          indicator.setAttribute('data-gutter-indicator', 'true')
          indicator.style.cssText = `

           `
          gutter.appendChild(indicator)
          return indicator
        }

        const gutter = document.querySelector(
          '.gutter-horizontal'
        ) as HTMLElement

        createIndicator(gutter)
      }
    }

    if (disabled && splitInstanceRef.current) {
      splitInstanceRef.current.destroy()
      splitInstanceRef.current = null
    } else if (!disabled && !splitInstanceRef.current) {
      initializeSplit()
    }

    return () => {
      if (splitInstanceRef.current) {
        splitInstanceRef.current.destroy()
        splitInstanceRef.current = null
      }
    }
  }, [disabled, getSavedProportions, saveProportions, minSizes, gutterSize])

  return {
    leftPanelRef,
    rightPanelRef,
    isSplitActive: !!splitInstanceRef.current,
  }
}
